import React, { type ComponentProps, useMemo } from 'react';
import { StyleSheet, type ViewProps, type ViewStyle } from 'react-native';
import { LI, UL } from '@expo/html-elements';
import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationSelectedIngredientsList = (
  props: CustomizationSelectedIngredientsListProps,
) => {
  const { children, style, ...restProps } = props;

  return (
    <UL style={[styles.container, style]} {...restProps}>
      {children}
    </UL>
  );
};

export const CustomizationSelectedIngredientsListItem = (
  props: CustomizationSelectedIngredientsListItemProps,
) => {
  const { children, style, isActive, ...restProps } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const dynamicStyles = useMemo<ViewStyle>(
    () => ({ zIndex: isActive ? 1 : undefined }),
    [isActive],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <LI style={[styles.listItem, dynamicStyles, style]} {...restProps}>
      {children}
    </LI>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    // NOTE: Those styles are required since it's not a default style of the web
    // `ul` element

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    position: 'relative',

    gap: theme.spacing['3'],
  },
  listItem: {
    // NOTE: Those styles are required since it's not a default style of the web
    // `li` element

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    position: 'relative',
    width: '100%',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationSelectedIngredientsListProps = ComponentProps<typeof UL>;

type CustomizationSelectedIngredientsListItemProps = {
  // NOTE: For some reason, TS was not happy with `LI` component's default styles.
  style?: ViewProps['style'];
  isActive?: boolean;
} & Omit<ComponentProps<typeof LI>, 'style'>;
